import { useCallback, useEffect, useMemo } from 'react';
import differenceInHours from 'date-fns/differenceInHours';

import { ADS_MAX_PUBSTREAM_VIEWS_TIER_1_2, ADS_MAX_PUBSTREAM_VIEWS_TIER_3 } from 'constants/limits';
import { selectAdSession } from 'redux/ads/selectors';
import { AdSession, setAdSession } from 'redux/ads/reducer';
import { LS_AD_SESSION_KEY } from 'utils/localStorage';

import { useAppDispatch, useAppSelector } from './redux';

declare global {
    interface Window {
        tier: number;
    }
}

export type AdProvider = 'adsense' | 'pubstream';

const AD_SESSION_LEN_HOURS = 2;

const enabledProviders: AdProvider[] = ['pubstream'];

const useAdSessionManager = () => {
    const dispatch = useAppDispatch();
    const reduxAdSession = useAppSelector(selectAdSession);

    // Tier is stored in 'window' because it is used in index.html before react is loaded
    const tier = Number(window.tier);

    const localStorageSession = () => {
        const localStorageVal = localStorage.getItem(LS_AD_SESSION_KEY);

        return localStorageVal ? (JSON.parse(localStorageVal) as AdSession) : null;
    };

    const adSession = reduxAdSession || localStorageSession();

    useEffect(() => {
        if (!reduxAdSession) {
            initReduxSession();
        }
    }, [reduxAdSession]);

    useEffect(() => {
        checkSessionExpired();
    }, [adSession]);

    const initReduxSession = () => {
        const savedSession = localStorageSession();

        if (!savedSession?.pageViewCount || !savedSession?.sessionStartDateISO) {
            resetSession();
        } else if (!reduxAdSession) {
            dispatch(setAdSession(savedSession));
        }
    };

    const checkSessionExpired = () => {
        if (
            adSession &&
            differenceInHours(new Date(), new Date(adSession.sessionStartDateISO)) >= AD_SESSION_LEN_HOURS
        ) {
            resetSession();
        }
    };

    const updateSession = (session: AdSession) => {
        localStorage.setItem(LS_AD_SESSION_KEY, JSON.stringify(session));
        dispatch(setAdSession(session));
    };

    const resetSession = () => {
        updateSession({
            pageViewCount: 0,
            sessionStartDateISO: new Date().toISOString(),
        });
    };

    const incrPageViewCount = useCallback(() => {
        if (!adSession) return;

        updateSession({
            ...adSession,
            pageViewCount: adSession.pageViewCount + 1,
        });
    }, [adSession]);

    const currentAdProvider = useMemo((): AdProvider => {
        const count = adSession?.pageViewCount || 0;
        const isAdsenseEnabled = enabledProviders.includes('adsense');

        let useAdsense;
        switch (tier) {
            case 1:
            case 2:
                useAdsense = isAdsenseEnabled && count >= ADS_MAX_PUBSTREAM_VIEWS_TIER_1_2;
                break;
            case 3:
                useAdsense = isAdsenseEnabled && count >= ADS_MAX_PUBSTREAM_VIEWS_TIER_3;
                break;
            default:
                useAdsense = isAdsenseEnabled;
        }

        return useAdsense ? 'adsense' : 'pubstream';
    }, [adSession, tier]);

    return {
        incrPageViewCount,
        currentAdProvider,
        resetSession,
        adSession,
        tier,
    };
};

export default useAdSessionManager;
